import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { supabase } from "../../config/Supabase";
import TitleLineNoLink from "../utils/TitleLineNoLink";
import TitleLine from "../utils/TitleLine";
import Featured from "./Featured";
import SEO from "../utils/SEO";

// The library now reads PUBLISHED articles from Supabase (the CMS), grouped by
// category, linking to /learn/post/<slug>. Publishing an article in the admin
// makes it appear here automatically — no more hardcoded JSON sections.

const CATEGORY_LABELS = {
  herbalism: "Basics of Medicinal Herbs",
  recipes: "Everyday Recipes",
  "mental-health": "Mental Health",
  "digestive-health": "Digestive Health",
  ayurveda: "Ayurveda",
  "womens-health": "Women's Health",
  "weight-loss": "Weight Loss",
  "heart-health": "Heart Health",
  diabetes: "Diabetes",
  cancer: "Cancer",
};
const labelFor = (c) =>
  CATEGORY_LABELS[c] ||
  (c ? c.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) : "Articles");

const truncate = (t, n) => (t && t.length > n ? `${t.slice(0, n)}…` : t || "");

const ArticleCard = ({ article }) => (
  <Link
    to={`/learn/post/${article.slug}`}
    style={{ textDecoration: "none", color: "inherit" }}
  >
    <Card style={{ border: "none", borderRadius: "15px", marginBottom: "50px", padding: "15px" }}>
      <Card.Img
        variant="top"
        src={article.hero_image_url || "/img/og-image.jpg"}
        alt={article.title}
        style={{
          height: "200px",
          objectFit: "cover",
          borderTopLeftRadius: "15px",
          borderTopRightRadius: "15px",
        }}
      />
      <Card.Body>
        <Card.Title>{article.title}</Card.Title>
        <Card.Text>{truncate(article.summary, 100)}</Card.Text>
      </Card.Body>
    </Card>
  </Link>
);

const Grid = ({ articles }) => (
  <Container>
    <Row>
      {articles.map((a) => (
        <Col xs={12} md={4} key={a.slug}>
          <ArticleCard article={a} />
        </Col>
      ))}
    </Row>
  </Container>
);

function LibraryHome() {
  const [articles, setArticles] = useState([]);
  const [catLabels, setCatLabels] = useState({});
  const [loading, setLoading] = useState(true);

  // Display names for categories come from the managed table (fallback: constant).
  useEffect(() => {
    supabase
      .from("article_categories")
      .select("slug,name")
      .then(({ data }) => {
        if (data) setCatLabels(Object.fromEntries(data.map((c) => [c.slug, c.name])));
      });
  }, []);
  const label = (c) => catLabels[c] || labelFor(c);

  useEffect(() => {
    let active = true;
    (async () => {
      let { data, error } = await supabase
        .from("articles")
        .select("slug,title,summary,hero_image_url,category,published_at,featured,featured_rank")
        .eq("status", "published")
        .order("published_at", { ascending: false, nullsFirst: false });
      // Fallback if the featured columns aren't there yet (migration not applied).
      if (error) {
        ({ data, error } = await supabase
          .from("articles")
          .select("slug,title,summary,hero_image_url,category,published_at")
          .eq("status", "published")
          .order("published_at", { ascending: false, nullsFirst: false }));
      }
      if (!active) return;
      if (!error && data) setArticles(data);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  // Group by category, preserving publish-date order within each group.
  const byCategory = {};
  for (const a of articles) {
    const key = a.category || "other";
    (byCategory[key] = byCategory[key] || []).push(a);
  }
  const categories = Object.keys(byCategory).sort(
    (a, b) => byCategory[b].length - byCategory[a].length
  );
  // Featured = hand-picked (featured flag), ordered by featured_rank.
  // Falls back to the 6 most recent so the hero is never empty.
  const flagged = articles
    .filter((a) => a.featured)
    .sort((a, b) => (a.featured_rank ?? 999) - (b.featured_rank ?? 999));
  const featured = (flagged.length ? flagged : articles).slice(0, 6);

  return (
    <div>
      <SEO
        title="Medicinal Herb & Natural Remedy Library - Sarv"
        description="Explore medicinal herbs and evidence-based natural remedies. Articles on herbal medicine, sleep, mental health, women's health, digestion, and Ayurveda written by holistic health experts."
        canonicalPath="/learn"
        keywords="medicinal herbs library, herbal medicine articles, natural remedies guide, Ayurveda articles, holistic health library, herbs for anxiety, herbs for sleep, herbs for women, digestive health herbs, herbal medicine encyclopedia"
      />

      {loading ? (
        <Container className="text-center" style={{ padding: "120px 0" }}>
          <Spinner animation="border" />
        </Container>
      ) : articles.length === 0 ? (
        <Container className="text-center" style={{ padding: "120px 0", minHeight: "50vh" }}>
          <h1>Health Library</h1>
          <p style={{ color: "#777" }}>New articles are on the way — check back soon.</p>
        </Container>
      ) : (
        <>
          {/* Featured hero — hand-picked in the admin (featured flag) */}
          <TitleLineNoLink title="Featured" />
          <Featured articles={featured} />
          <div className="content" style={{ paddingBottom: "30px" }} />

          {/* One section per category, capped at 6 with a "View All" link */}
          {categories.map((cat) => (
            <div key={cat}>
              <TitleLine title={label(cat)} to={`/learn/category/${cat}`} />
              <Grid articles={byCategory[cat].slice(0, 6)} />
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export default LibraryHome;
